import React, { useState, useEffect, useMemo } from "react"
import { DateRange } from "react-day-picker"
import { format, getYear, setMonth, setYear } from "date-fns"
import { MonthYearDropdown } from "./MonthYearDropdown"

import { Calendar } from "app/components/ui/calendar"
import { Button } from "app/components/ui/button"

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const years = Array.from({ length: 101 }, (_, i) => getYear(new Date()) - 100 + i)

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onDateRangeChange?: (range: DateRange | undefined) => void
  startDate?: Date | null
  endDate?: Date | null
  isOpen?: boolean
  setIsOpen?: (open: boolean) => void
  onConfirm?: (startDate: Date | null, endDate: Date | null) => void
}

export const DateFilter = ({ 
  className, 
  onDateRangeChange,
  startDate,
  endDate,
  isOpen,
  setIsOpen,
  onConfirm
}: Props) => {
  // Temporary state for date selection
  const [tempDate, setTempDate] = useState<DateRange | undefined>(() => ({
    from: startDate || undefined,
    to: endDate || undefined
  }))

  const [selectionMode, setSelectionMode] = useState<'start' | 'end'>('start')
  const [lastSelectedDate, setLastSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedMonth, setSelectedMonth] = useState<string>(months[new Date().getMonth()])
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())

  /// Update internal state when props change
  useEffect(() => {
    setTempDate({
      from: startDate || undefined,
      to: endDate || undefined
    })
    
    // Reset selection mode based on current state
    if (!startDate && !endDate) {
      setSelectionMode('start')
      setLastSelectedDate(undefined)
    } else if (startDate && !endDate) {
      setSelectionMode('end')
      setLastSelectedDate(startDate)
    } else if (startDate && endDate) {
      // Both dates are set - don't reset selection mode to avoid interfering with user selection
      setLastSelectedDate(endDate)
    }
  }, [startDate, endDate])

  const handleDateChange = (selectedDate: Date | undefined) => {
    
    if (!selectedDate) {
      // If no date selected, clear everything and start over
      setTempDate(undefined)
      setSelectionMode('start')
      setLastSelectedDate(undefined)
      return
    }

    // Always update the last selected date for calendar display
    setLastSelectedDate(selectedDate)

    if (selectionMode === 'start') {
      // First selection - set start date
      const updatedDate = {
        from: selectedDate,
        to: undefined
      }
     
      setTempDate(updatedDate)
      setSelectionMode('end')
    } else {
      // Second selection - set end date
      // For same date selection, we need to handle it specially
      const isSameDate = tempDate?.from && selectedDate && 
        tempDate.from.getTime() === selectedDate.getTime()
      
      if (isSameDate) {
        // Same date selected - set both start and end to the same date
        const updatedDate = {
          from: selectedDate,
          to: selectedDate
        }
      
        setTempDate(updatedDate)
        // Switch back to start mode for new selection cycle
        setSelectionMode('start')
      } else {
        // Different date selected
        const updatedDate = {
          from: tempDate?.from,
          to: selectedDate
        }
       
        setTempDate(updatedDate)
        setSelectionMode('start') // Ready for next cycle
      }
    }
  }

  const handleConfirm = () => {
    // Apply the temporary dates to the parent component
    onConfirm?.(tempDate?.from || null, tempDate?.to || null)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  // Always show the last selected date in calendar for proper styling
  const selectedDate = useMemo(() => {
    return lastSelectedDate
  }, [lastSelectedDate])

  // Convert selected month and year to a Date object for Calendar
  const calendarMonth = useMemo(() => {
    const monthIndex = months.indexOf(selectedMonth);
    if (monthIndex !== -1) {
      const date = new Date();
      const newDate = setMonth(setYear(date, selectedYear), monthIndex);

      return newDate;
    }
    return new Date();
  }, [selectedMonth, selectedYear]);

  return (
    <div onClick={handleClick} onMouseDown={handleMouseDown}>
      {/* Display selected dates */}
      <div className="flex items-center bg-white h-[2.375rem]">
        <div className={`flex flex-col gap-1 justify-center items-center w-[7.15625rem] h-full px-2 py-1 ${selectionMode === 'start' && !tempDate?.from ? 'bg-[#F2F4F8]' : ''}`}>
          <span className="text-[#697077] text-[0.625rem] font-normal leading-[0.875rem] tracking-[-0.00625rem] whitespace-nowrap">
            {tempDate?.from ? 'Start Date' : 'Select Start Date'}
          </span>
          {tempDate?.from && (
            <span className="text-[#343A3F] text-xs font-medium leading-3 whitespace-nowrap">
              {format(tempDate.from, 'MM / dd / yy')}
            </span>
          )}
        </div>
        <div className="flex items-center justify-center p-1 w-3 bg-white text-[#697077] text-[0.625rem] font-normal leading-[0.6875rem] tracking-[-0.00625rem]">
          -
        </div>
        <div className={`flex flex-col gap-1 justify-center items-center w-[7.15625rem] h-full px-2 py-1 ${selectionMode === 'end' && !tempDate?.to ? 'bg-[#F2F4F8]' : ''}`}>
          <span className="text-[#697077] text-[0.625rem] font-normal leading-[0.875rem] tracking-[-0.00625rem] whitespace-nowrap">
            {tempDate?.to ? 'End Date' : 'Select End Date'}
          </span>
          {tempDate?.to && (
            <span className="text-[#343A3F] text-xs font-medium leading-3 whitespace-nowrap">
              {format(tempDate.to, 'MM / dd / yy')}
            </span>
          )}
        </div>
      </div>
      <div className="relative">
        <div className="flex absolute top-4 right-4 z-10 gap-4 justify-end items-center">
          <MonthYearDropdown
            selectedValue={selectedMonth}
            setSelectedValue={(value) => setSelectedMonth(value as string)}
            options={months}
            type="month"
          />
          <MonthYearDropdown
            selectedValue={selectedYear}
            setSelectedValue={(value) => setSelectedYear(value as number)}
            options={years}
            type="year"
          />
        </div>
        <Calendar
          mode="single"
          month={calendarMonth}
          selected={selectedDate}
          onDayClick={(day) => handleDateChange(day)}
          className="bg-white border-t-2 border-[#C1C7CD]"
          classNames={{
            nav: "hidden",
            caption_label: "hidden",
          }}
        />
      </div>
      {tempDate?.from && tempDate?.to && (
        <div className="flex justify-end p-4 pt-0 bg-white">
          <Button
            type="button"
            variant="ghost"
            className="w-fit bg-[#55B494] hover:bg-[#46A082] px-2 py-1 rounded-[0.375rem] text-white hover:text-white text-xs font-medium leading-[0.9rem]"
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  )
}
